import { useState, useMemo, useCallback } from 'react';

const defaultLimit = 60;

const defaultPaginationPropsValue = {
  pageNumber: 0,
  offset: 0,
  // maxPageNumber that the user has visited
  maxPageNumber: 0,
  canLoadMoreData: false,
};

/**
 * Currently only handles single list
 *
 * Supports both 0-based (indexBase=0) and 1-based (indexBase=1) pages.
 */
export const useTablePagination = (
  customLimit: number = defaultLimit,
  initialPageNumber = 0,
  indexBase: 0 | 1 = 0,
) => {
  const [paginationState, setPaginationProps] = useState(() => ({
    pageNumber: initialPageNumber,
    offset: Math.max(0, initialPageNumber - indexBase) * customLimit,
    maxPageNumber: initialPageNumber,
    canLoadMoreData: false,
  }));
  const { pageNumber, offset, maxPageNumber } = paginationState;

  const { startIndex, endIndex } = useMemo(() => {
    const startIndex = offset;
    const endIndex = startIndex + customLimit;

    return {
      startIndex,
      endIndex,
    };
  }, [offset, customLimit]);

  const handlePageChange = useCallback(
    ({ newPageNumber }: { newPageNumber: number }) => {
      // Offset 0: Page 0
      // Offset 50: Page 1
      // Offset 100: Page 2
      // Offset 150: Page 3
      // Offset 200: Page 4
      const newOffset = Math.max(0, newPageNumber - indexBase) * customLimit;

      // Compute based on prev to avoid stale closures and keep this callback stable.
      setPaginationProps((prev) => {
        const canLoadMoreData = newPageNumber > prev.maxPageNumber;
        return {
          ...prev,
          offset: newOffset,
          pageNumber: newPageNumber,
          maxPageNumber: canLoadMoreData ? newPageNumber : prev.maxPageNumber,
          canLoadMoreData,
        };
      });
    },
    [customLimit, indexBase],
  );

  const resetPagination = useCallback(() => {
    setPaginationProps({
      pageNumber: initialPageNumber,
      offset: Math.max(0, initialPageNumber - indexBase) * customLimit,
      maxPageNumber: initialPageNumber,
      canLoadMoreData: false,
    });
  }, [customLimit, initialPageNumber, indexBase]);

  return {
    pageNumber,
    handlePageChange,
    startIndex,
    endIndex,
    offset,
    limit: customLimit,
    resetPagination,
  };
};
